import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { GLOSSARY_TERMS, glossaryPath, type GlossaryTerm } from "@/lib/glossary/content";

import DefinitionBlock from "@/components/glossary/DefinitionBlock";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import FancyButton from "@/components/ui/fancy-button";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";
import { definedTerm } from "@/lib/seo/schema";
import { getMarketingPage } from "@/lib/seo/pages";

interface GlossaryTermPageProps {
  term: GlossaryTerm | null;
}

const EYEBROW = "text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]";

const GlossaryTermPage = ({ term: initialTerm }: GlossaryTermPageProps) => {
  const router = useRouter();
  const slug = typeof router.query.term === "string" ? router.query.term : "";
  const term = initialTerm ?? (slug ? (GLOSSARY_TERMS[slug] ?? null) : null);

  if (!term) {
    if (!router.isReady) return null;

    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">
          The glossary term you&apos;re looking for doesn&apos;t exist.
        </p>
        <FancyButton
          href="/glossary"
          semiFancy
          className="mt-8 w-fit rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
        >
          Browse the glossary
        </FancyButton>
      </div>
    );
  }

  const path = glossaryPath(slug);
  const crumbs = breadcrumbsForPath(path);
  const relatedTerms = term.relatedTerms
    .filter((related) => related !== slug && Boolean(GLOSSARY_TERMS[related]))
    .map((related) => ({ slug: related, label: GLOSSARY_TERMS[related].term }));
  const cta = getMarketingPage(term.related[0] ?? "");

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      <NextSeo title={`${term.seoTitle} | Crayo`} description={term.seoDescription} />
      <JsonLd
        nodes={pageGraph({
          path,
          name: term.seoTitle,
          description: term.seoDescription,
          crumbs,
          faqs: term.faqs,
          mainEntity: definedTerm({
            name: term.term,
            description: term.definition,
            url: path,
            setUrl: "/glossary",
          }),
          speakable: [".answer-first"],
        })}
      />
      <Breadcrumbs items={crumbs} />

      <section className="mx-auto flex w-full max-w-screen-2xl flex-col px-4 pb-6 pt-8 md:px-12 md:pt-10 lg:px-[120px]">
        <div className="flex w-full max-w-[760px] flex-col gap-8">
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>{term.category}</span>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-[#101828] sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
              {term.term}
            </h1>
          </div>

          <DefinitionBlock term={term.term} definition={term.definition} />

          <div className="flex flex-col gap-4 text-[15px] leading-[1.7] text-[#525866] sm:text-[16px]">
            {term.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          {term.examples && term.examples.length > 0 && (
            <div className="flex flex-col gap-3">
              <h2 className="text-[20px] font-semibold tracking-tight text-[#101828] sm:text-[22px]">
                Examples
              </h2>
              <ul className="flex flex-col gap-2.5">
                {term.examples.map((example) => (
                  <li
                    key={example}
                    className="flex gap-3 text-[15px] leading-[1.7] text-[#525866] sm:text-[16px]"
                  >
                    <span
                      className="mt-[10px] size-1.5 shrink-0 rounded-full bg-[#335CFF]"
                      aria-hidden
                    />
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {relatedTerms.length > 0 && (
            <div className="flex flex-col gap-3">
              <span className={EYEBROW}>Related terms</span>
              <div className="flex flex-wrap gap-2">
                {relatedTerms.map((related) => (
                  <Link
                    key={related.slug}
                    href={glossaryPath(related.slug)}
                    prefetch={false}
                    className="rounded-full border border-[#EFF2FF] bg-white px-3.5 py-1.5 text-[13px] font-medium text-[#525866] shadow-sm transition-colors hover:border-[#335CFF] hover:text-[#335CFF]"
                  >
                    {related.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {cta && (
            <Link
              href={cta.path}
              prefetch={false}
              className="group flex flex-col gap-2 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-5 py-4 transition-colors hover:border-[#335CFF] sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-8"
            >
              <span className="text-[15px] font-medium text-[#101828]">
                Do this in Crayo with {cta.title}
              </span>
              <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-[#335CFF]">
                Take a look
                <ArrowRight
                  className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  strokeWidth={2.5}
                />
              </span>
            </Link>
          )}
        </div>
      </section>

      <RelatedPages path={path} />
      <FAQ items={term.faqs} />
      <Footer />
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: Object.keys(GLOSSARY_TERMS).map((term) => ({ params: { term } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<GlossaryTermPageProps> = async ({ params }) => {
  const slug = params?.term as string;
  const term = GLOSSARY_TERMS[slug] || null;

  return { props: { term } };
};

export default GlossaryTermPage;
